import {
  CommandResponseMode,
  DeviceCommand,
  ITxTransport,
} from "lib/deviceDrivers/sessionFramework/device/types";

import { sendCommand } from "./framing";

const TIMEOUT_ERROR_CODE = "TIMEOUT";
const BUSY_RETRY_BASE_DELAY_MS = 250;
const BUSY_RETRY_MAX_DELAY_MS = 2000;

interface PendingCommand extends DeviceCommand {
  retryCount: number;
}

export interface CommandQueueState<TFrame> {
  commandQueue: DeviceCommand[];
  pendingCommand?: PendingCommand;
  commandTimeout?: ReturnType<typeof setTimeout>;
  commandRetryDelayTimeout?: ReturnType<typeof setTimeout>;
  isFlushingQueue: boolean;
  matchesFrame: (responseMode: CommandResponseMode, frame: TFrame) => boolean;
  isRetryableError: (code: string) => boolean;
}

export function createCommandQueueState<TFrame>(
  matchesFrame: (responseMode: CommandResponseMode, frame: TFrame) => boolean,
  isRetryableError: (code: string) => boolean,
): CommandQueueState<TFrame> {
  return {
    commandQueue: [],
    isFlushingQueue: false,
    matchesFrame,
    isRetryableError,
  };
}

/**
 * Appends commands to the queue and immediately starts flushing if nothing is pending.
 * The `onFatalFailure` callback is invoked with an error code if a command times out or fails unrecoverably.
 */
export function queueCommands<TFrame>(
  state: CommandQueueState<TFrame>,
  tx: ITxTransport,
  commands: DeviceCommand[],
  onFatalFailure: (code: string) => void,
): void {
  if (commands.length === 0) {
    return;
  }

  state.commandQueue.push(...commands);
  flushCommandQueue(state, tx, onFatalFailure);
}

/**
 * Checks whether the incoming error code is a retryable busy response and backs off if so.
 * Non-retryable errors and exhausted busy retries reset the pipeline and invoke `onFatalFailure`.
 */
export function handleIncomingErrorCode<TFrame>(
  state: CommandQueueState<TFrame>,
  tx: ITxTransport,
  code: string,
  onFatalFailure: (code: string) => void,
): void {
  if (
    state.pendingCommand?.retryOnBusy &&
    state.isRetryableError(code) &&
    retryPendingCommandWithBackoff(state, tx, onFatalFailure)
  ) {
    return;
  }

  resetCommandPipeline(state);
  onFatalFailure(code);
}

/**
 * Clears the pending command when the incoming frame satisfies its expected response mode.
 * This releases the queue so the next command can be sent.
 */
export function tryResolvePendingCommand<TFrame>(
  state: CommandQueueState<TFrame>,
  tx: ITxTransport,
  frame: TFrame,
  onFatalFailure: (code: string) => void,
): void {
  const { pendingCommand } = state;
  if (!pendingCommand) {
    return;
  }

  if (!state.matchesFrame(pendingCommand.responseMode, frame)) {
    return;
  }

  clearPendingCommand(state);
  flushCommandQueue(state, tx, onFatalFailure);
}

/**
 * Cancels all queued and in-flight work and clears both retry timers.
 * Call this before changing protocol mode or when tearing down the connection.
 */
export function resetCommandPipeline<TFrame>(
  state: CommandQueueState<TFrame>,
): void {
  clearCommandTimeout(state);
  clearCommandRetryDelayTimeout(state);
  state.commandQueue.length = 0;
  state.pendingCommand = undefined;
}

function flushCommandQueue<TFrame>(
  state: CommandQueueState<TFrame>,
  tx: ITxTransport,
  onFatalFailure: (code: string) => void,
): void {
  if (state.isFlushingQueue || state.pendingCommand) {
    return;
  }

  state.isFlushingQueue = true;

  try {
    while (!state.pendingCommand && state.commandQueue.length > 0) {
      const nextCommand = state.commandQueue.shift();
      if (!nextCommand) {
        return;
      }

      if (nextCommand.responseMode === CommandResponseMode.NONE) {
        sendCommand(tx, nextCommand.payload);
        continue;
      }

      state.pendingCommand = { ...nextCommand, retryCount: 0 };
      schedulePendingCommandTimeout(state, tx, onFatalFailure);
      sendCommand(tx, nextCommand.payload);
      return;
    }
  } finally {
    state.isFlushingQueue = false;
  }
}

function schedulePendingCommandTimeout<TFrame>(
  state: CommandQueueState<TFrame>,
  tx: ITxTransport,
  onFatalFailure: (code: string) => void,
): void {
  clearCommandTimeout(state);
  const { pendingCommand } = state;
  if (!pendingCommand) {
    return;
  }

  state.commandTimeout = setTimeout(() => {
    state.commandTimeout = undefined;
    onPendingCommandTimeout(state, tx, onFatalFailure);
  }, pendingCommand.timeoutMs);
}

function onPendingCommandTimeout<TFrame>(
  state: CommandQueueState<TFrame>,
  tx: ITxTransport,
  onFatalFailure: (code: string) => void,
): void {
  const { pendingCommand } = state;
  if (!pendingCommand) {
    return;
  }

  if (pendingCommand.retryCount < pendingCommand.maxRetries) {
    retryPendingCommand(state, tx, onFatalFailure);
    return;
  }

  resetCommandPipeline(state);
  onFatalFailure(TIMEOUT_ERROR_CODE);
}

function retryPendingCommand<TFrame>(
  state: CommandQueueState<TFrame>,
  tx: ITxTransport,
  onFatalFailure: (code: string) => void,
): void {
  const { pendingCommand } = state;
  if (!pendingCommand) {
    return;
  }

  pendingCommand.retryCount += 1;
  clearCommandTimeout(state);
  schedulePendingCommandTimeout(state, tx, onFatalFailure);
  sendCommand(tx, pendingCommand.payload);
}

function retryPendingCommandWithBackoff<TFrame>(
  state: CommandQueueState<TFrame>,
  tx: ITxTransport,
  onFatalFailure: (code: string) => void,
): boolean {
  const { pendingCommand } = state;
  if (!pendingCommand) {
    return false;
  }

  if (pendingCommand.retryCount >= pendingCommand.maxRetries) {
    return false;
  }

  const backoffMs = computeBusyRetryBackoffMs(pendingCommand.retryCount);
  pendingCommand.retryCount += 1;
  clearCommandTimeout(state);
  clearCommandRetryDelayTimeout(state);

  state.commandRetryDelayTimeout = setTimeout(() => {
    state.commandRetryDelayTimeout = undefined;

    if (state.pendingCommand !== pendingCommand) {
      return;
    }

    schedulePendingCommandTimeout(state, tx, onFatalFailure);
    sendCommand(tx, pendingCommand.payload);
  }, backoffMs);

  return true;
}

function clearPendingCommand<TFrame>(state: CommandQueueState<TFrame>): void {
  clearCommandTimeout(state);
  state.pendingCommand = undefined;
}

function clearCommandTimeout<TFrame>(state: CommandQueueState<TFrame>): void {
  if (state.commandTimeout) {
    clearTimeout(state.commandTimeout);
    state.commandTimeout = undefined;
  }
}

function clearCommandRetryDelayTimeout<TFrame>(
  state: CommandQueueState<TFrame>,
): void {
  if (state.commandRetryDelayTimeout) {
    clearTimeout(state.commandRetryDelayTimeout);
    state.commandRetryDelayTimeout = undefined;
  }
}

function computeBusyRetryBackoffMs(retryCount: number): number {
  return Math.min(
    BUSY_RETRY_BASE_DELAY_MS * 2 ** retryCount,
    BUSY_RETRY_MAX_DELAY_MS,
  );
}
